import { useEffect, useRef } from 'react'

import { Alert, Box, Icon, Text } from '@factorialco/deprecated-design-system'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledInput from 'modules/core/components/LabeledInput'
import MoneyField from 'modules/core/components/MoneyField'
import NumberField from 'modules/core/components/NumberField'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { FormErrors } from 'modules/core/hooks/useForm'
import i18n from 'modules/core/lib/i18n'

import { budgetTypesEnum } from 'modules/project_management/lib/constants'

export interface IProjectBudgetValues {
  budgetType: budgetTypesEnum
  budgetAllocation: number | null | undefined
  feeAmountCents: number | null | undefined
  currencyType: string
  plannedCents: number | null | undefined
  plannedMinutes: number | null | undefined
}

export interface IProjectBudgetFormProps {
  onChangeValue: (
    fieldName: string,
    fieldValue: number | string | undefined,
    shouldValidate?: boolean
  ) => void
  values: IProjectBudgetValues
  isOwner: boolean
  errors: FormErrors<IProjectBudgetValues>
  isBillable: boolean
  hasBudgetFeature: boolean
  hasCostsFeature: boolean
}

const ProjectBudgetForm = ({
  onChangeValue,
  values,
  errors,
  isOwner,
  isBillable,
  hasBudgetFeature,
  hasCostsFeature,
}: IProjectBudgetFormProps) => {
  const prevIsBillableRef = useRef(isBillable)

  useEffect(() => {
    if (prevIsBillableRef.current === true && isBillable === false) {
      onChangeValue('feeAmountCents', undefined)
    }
    prevIsBillableRef.current = isBillable
  }, [isBillable, onChangeValue])

  const isProjectFixedCostBudgetSelected = values.budgetType === budgetTypesEnum.projectFixedCost
  const isTotalBudgetSelected = values.budgetType === budgetTypesEnum.totalBudget
  const canSeeCosts = isOwner

  const cost = values.plannedCents?.valueOf() || 0
  const income = values.feeAmountCents?.valueOf() || 0

  const isCostValidNumber = !isNaN(cost) && !(cost <= 0)
  const isIncomeValidNumber = !isNaN(income) && !(income <= 0)

  const validBenefit = isCostValidNumber && isIncomeValidNumber

  const monetaryProfit = income - cost
  const percentageProfit = (monetaryProfit / income) * 100

  const isPlanningDefined = values.plannedCents && values.feeAmountCents && validBenefit
  const currencyType = values.currencyType

  const hasProjectFixedCostBudget = hasBudgetFeature && hasCostsFeature && canSeeCosts

  const selectableOptions: Array<{
    label: string
    value: budgetTypesEnum
    description: string
    disabled?: boolean
  }> = [
    {
      label: i18n.t(
        'project_management.forms.project_planning.project_budget.options.no_budget.title'
      ),
      value: budgetTypesEnum.withoutBudget,
      description: i18n.t(
        'project_management.forms.project_planning.project_budget.options.no_budget.description'
      ),
    },
  ]

  if (hasProjectFixedCostBudget) {
    selectableOptions.push(
      {
        label: i18n.t(
          'project_management.forms.project_planning.project_budget.options.fixed_price.title'
        ),
        value: budgetTypesEnum.projectFixedCost,
        disabled: !isOwner,
        description: i18n.t(
          'project_management.forms.project_planning.project_budget.options.fixed_price.description'
        ),
      },
      {
        label: i18n.t(
          'project_management.forms.project_planning.project_budget.options.total_budget.title'
        ),
        value: budgetTypesEnum.totalBudget,
        disabled: !isOwner,
        description: i18n.t(
          'project_management.forms.project_planning.project_budget.options.total_budget.description'
        ),
      }
    )
  }

  return (
    <Box gap="s16">
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <LabeledInput
            isLegend
            label={i18n.t('project_management.forms.project_planning.project_budget.title')}
          >
            <Box
              gap="s8"
              background="white"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
              border={{
                all: {
                  width: 's2',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
              borderRadius={{
                all: 'abs008',
              }}
              padding="s8"
              paddingX="s16"
              cursor="pointer"
            >
              <Text.Normal ellipsis>
                {selectableOptions.find((option) => option.value === values.budgetType)?.label}
              </Text.Normal>
              <Icon.Small icon="caret-down" color="grey600" />
            </Box>
          </LabeledInput>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="center" width="s400">
          {selectableOptions.map((option) => (
            <PopoverMenu.TouchableOption
              key={option.value}
              title={option.label}
              description={option.description}
              onClick={() => onChangeValue('budgetType', option.value)}
              disabled={option.disabled}
            />
          ))}
        </PopoverMenu.Content>
      </PopoverMenu>

      {isProjectFixedCostBudgetSelected && (
        <>
          <LabeledInput
            isLegend
            label={i18n.t('project_management.planning.planned_cost')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('project_management.planning.planned_cost_tooltip')}
              />
            }
          >
            <MoneyField
              hideLabel
              value={values.plannedCents?.toString()}
              currency={currencyType}
              onChange={(value) => {
                onChangeValue('plannedCents', value?.toString() || '0', true)
              }}
              error={errors.plannedCents}
              maxDecimals={2}
            />
          </LabeledInput>
          {isBillable && (
            <Box gap="s24">
              <LabeledInput
                isLegend
                label={i18n.t('project_management.planning.planned_income')}
                secondaryLabel={
                  <InformationTooltip
                    information={i18n.t(
                      'project_management.modals.create_project.wizard.step_three.income_alert'
                    )}
                  />
                }
              >
                <MoneyField
                  hideLabel
                  value={values.feeAmountCents?.toString() || '0'}
                  currency={currencyType}
                  onChange={(value) => {
                    onChangeValue('feeAmountCents', value?.toString() || '0', true)
                  }}
                  error={errors.feeAmountCents}
                  maxDecimals={2}
                />
              </LabeledInput>
              {isPlanningDefined && (
                <Alert
                  color={monetaryProfit < 0 ? 'error' : monetaryProfit === 0 ? 'info' : 'highlight'}
                >
                  {i18n.t('project_management.modals.create_project.wizard.step_three.alert', {
                    monetary_profit: monetaryProfit.toFixed(2),
                    percentage_profit: Math.round(percentageProfit),
                  })}
                </Alert>
              )}
            </Box>
          )}
        </>
      )}
      {isTotalBudgetSelected && (
        <>
          {isBillable && (
            <NumberField
              label={i18n.t(
                'project_management.forms.project_planning.project_budget.labels.total_budget.title'
              )}
              description={i18n.t(
                'project_management.forms.project_planning.project_budget.labels.total_budget.description'
              )}
              value={values.plannedCents || null}
              onChange={(value) => {
                onChangeValue('plannedCents', value?.toString() || '0', true)
              }}
              error={errors.plannedCents}
              maxDecimals={2}
              placeholder="0"
            />
          )}
          <NumberField
            label={i18n.t(
              'project_management.forms.project_planning.project_budget.labels.planned_hours'
            )}
            value={values.plannedMinutes || null}
            onChange={(value) => {
              onChangeValue('plannedMinutes', value?.toString() || '0', true)
            }}
            error={errors.plannedMinutes}
            maxDecimals={0}
            placeholder="0"
          />
        </>
      )}
    </Box>
  )
}

export default ProjectBudgetForm
